import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { TestimonialList } from '../components/testimonials';
import TestimonialForm from '../components/testimonials/TestimonialForm';
import GalleryItem from '../components/gallery/GalleryItem';
import useGallery from '../hooks/useGallery';
import SkewedBox from '../components/common/SkewedBox';
import SkewedButton from '../components/common/SkewedButton';

const HomePage: React.FC = () => {
  // Fetch the 3 most recent gallery images
  const { images, loading } = useGallery({ sortBy: 'newest' });
  
  // Get only the first 3 images
  const recentImages = images.slice(0, 3);
  
  // Get featured image for hero section without the Date.now() in the dependency
  const { 
    images: featuredImages, 
    loading: featuredLoading,
    refreshImagesFromServer 
  } = useGallery({ 
    featured: true
  });

  // Force refresh only once when component mounts
  React.useEffect(() => {
    console.log('HomePage mounted, fetching featured images once');
    // We'll avoid an explicit call to refreshImagesFromServer() 
    // since it's already fetched when the component mounts
    
    // Add a one-time refresh after a delay only if needed in the future
    const shouldRefresh = false; // Toggle this if we need an extra refresh
    
    if (shouldRefresh) {
      const timer = setTimeout(() => {
        console.log('Performing one-time refresh of featured images');
        refreshImagesFromServer();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []); // Empty dependency array so it only runs on mount

  // Log featured images for debugging
  React.useEffect(() => {
    console.log('HomePage - Featured images:', featuredImages);
  }, [featuredImages]);

  return (
    <Layout>
      {/* Hero Section */}
      <div className="bg-light">
        <div className="container mx-auto px-4 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black mb-8 text-black leading-tight">
                Transforming Spaces Through Art
              </h1>
              <p className="text-xl mb-10 text-gray-700 leading-relaxed">
                Custom murals that breathe life into your spaces. From residential
                to commercial projects, each mural is designed to inspire and transform.
              </p>
              <div className="flex flex-wrap gap-4">
                <SkewedButton to="/gallery" variant="primary">
                  Explore Gallery
                </SkewedButton>
                <SkewedButton to="/contact" variant="secondary">
                  Get in Touch
                </SkewedButton>
              </div>
            </div>
            <div className="overflow-hidden">
              {featuredLoading ? (
                <div className="bg-gray-100 h-80 md:h-96 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black" role="status" aria-label="Loading"></div>
                </div>
              ) : featuredImages.length > 0 ? (
                <div className="relative h-80 md:h-96">
                  <img
                    src={featuredImages[0].imageUrl}
                    alt={featuredImages[0].title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="bg-gray-200 h-80 md:h-96 flex items-center justify-center">
                  <p className="text-gray-500">No featured image selected</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Services Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-black text-center mb-16 text-black">Services</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <SkewedBox className="text-center" borderWidth={2} borderColor="black">
              <h3 className="text-2xl font-bold mb-4">Residential Murals</h3>
              <p className="text-gray-700 leading-relaxed">
                Transform your home with custom murals that reflect your personality
                and style. Perfect for living rooms, bedrooms, nurseries, and more.
              </p>
            </SkewedBox>

            <SkewedBox className="text-center" borderWidth={2} borderColor="black">
              <h3 className="text-2xl font-bold mb-4">Commercial Spaces</h3>
              <p className="text-gray-700 leading-relaxed">
                Make your business stand out with eye-catching murals. Ideal for offices,
                restaurants, retail spaces, and corporate environments.
              </p>
            </SkewedBox>

            <SkewedBox className="text-center" borderWidth={2} borderColor="black">
              <h3 className="text-2xl font-bold mb-4">Custom Design</h3>
              <p className="text-gray-700 leading-relaxed">
                From concept to completion, I work closely with you to create a
                unique design that perfectly suits your space and vision.
              </p>
            </SkewedBox>

            <SkewedBox className="text-center" borderWidth={2} borderColor="black">
              <h3 className="text-2xl font-bold mb-4">
                <Link to="/ar" className="hover:opacity-70 transition-opacity">
                  Augmented Reality
                </Link>
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Bring murals to life with AR technology. I can add interactive AR experiences
                to both new original murals and enhance existing artwork you want to elevate.
              </p>
            </SkewedBox>
          </div>
        </div>
      </div>
      
      {/* Recent Work */}
      <div className="py-20 bg-light">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-black text-center mb-6 text-black">Recent Work</h2>
          <p className="text-center text-gray-600 mb-16 max-w-2xl mx-auto text-lg">
            Here's a sample of my recent mural projects. Visit the gallery to see more of my work.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {loading ? (
              <div className="col-span-3 flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black" role="status" aria-label="Loading"></div>
              </div>
            ) : recentImages.length > 0 ? (
              recentImages.map((image) => (
                <GalleryItem
                  key={image.id}
                  image={image}
                  onClick={() => window.location.href = `/gallery?id=${image.id}`}
                />
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-500">No recent work to display. Please check the gallery for all projects.</p>
              </div>
            )}
          </div>

          <div className="text-center">
            <SkewedButton to="/gallery" variant="primary">
              View All Work
            </SkewedButton>
          </div>
        </div>
      </div>
      
      {/* Testimonials Section */}
      <TestimonialList />
      
      {/* Testimonial Submission Section */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-6 text-primary">Share Your Experience</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Worked with me on a mural project? I'd love to hear about your experience! 
              Your testimonial helps others understand the value and quality of my work.
            </p>
            <div className="bg-light p-8 rounded-lg shadow-md">
              <TestimonialForm />
            </div>
          </div>
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="py-20 bg-white border-y-2 border-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-black mb-6 text-black">Ready to Transform Your Space?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-700 leading-relaxed">
            Let's discuss your vision and create something amazing together. Contact me to schedule
            a consultation and get started on your mural project.
          </p>
          <SkewedButton to="/contact" variant="primary">
            Get Started
          </SkewedButton>
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;